import { Injectable } from "@angular/core";
import {
  Auth,
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  User,
} from "@angular/fire/auth";
import { Functions, httpsCallable } from "@angular/fire/functions";
import { Router } from "@angular/router";
import {
  FUNCTION_RESULT_STATUS,
  FunctionResult,
  IBasicPlatformUser,
} from "@nova/common-interfaces";
import { BehaviorSubject, filter, first, lastValueFrom } from "rxjs";
import { Claims } from "./claims.class.mjs";
import { PathService } from "./path.service";

@Injectable({ providedIn: "root" })
export class AuthService {
  // #region Properties (11)

  private _isReady = new BehaviorSubject<boolean>(false);
  private _photoUrl = new BehaviorSubject<string | null>(null);
  private _uid = new BehaviorSubject<string | null>(null);
  private isLoggedInSubject = new BehaviorSubject<boolean>(false);
  private userSubject = new BehaviorSubject<IBasicPlatformUser | null>(null);

  public claims: Claims = new Claims();
  isLoggedIn$ = this.isLoggedInSubject.asObservable();
  isReady$ = this._isReady.asObservable();
  photoUrl$ = this._photoUrl.asObservable();
  uid$ = this._uid.asObservable();
  user$ = this.userSubject.asObservable();

  private extractSubdomain(): string {
    const hostname = window.location.hostname;
    const parts = hostname.split(".");
    return parts[0];
  }

  // #endregion Properties (11)

  // #region Constructors (1)

  constructor(
    private auth: Auth,
    private functions: Functions,
    private router: Router,
    private pathService: PathService
  ) {
    onAuthStateChanged(this.auth, async (user) => {
      const subDomain = this.extractSubdomain();
      if (subDomain === "www" || subDomain === "order") {
        return;
      }

      console.log("CHECKING", user);

      if (user) {
        this._photoUrl.next(user.photoURL);
        await user
          .getIdToken()
          .then(async (idToken) => {
            const callable = httpsCallable<any, FunctionResult<IBasicPlatformUser>>(
              this.functions,
              "authentication-login-login"
            );
            await callable({ token: idToken })
              .then(
                async (result) => {
                  const loggedInStatus = auth.currentUser !== null;
                  this.isLoggedInSubject.next(loggedInStatus);
                  if (result.data.status === FUNCTION_RESULT_STATUS.SUCCESS) {
                    this.userSubject.next(result.data.data!);
                    console.log("URL", window.location);

                    if (this.pathService.redirect) {
                      this.pathService.redirectNow(this.router);
                    } else if (
                      this.router.url.includes("/login") ||
                      this.router.url === "/"
                    ) {
                      if (window.location.hostname.startsWith("billing")) {
                        this.router.navigateByUrl("/billing/overview");
                      } else if (window.location.hostname.startsWith("order")) {
                        this.router.navigateByUrl("/order/overview");
                      } else if (window.location.hostname.startsWith("admin")) {
                        this.router.navigateByUrl("/admin/overview");
                      } else {
                        this.router.navigateByUrl("/home");
                      }
                    }
                  } else {
                    console.error("Result not valid - Function failed", result);
                    this.userSubject.next(null);
                  }
                },
                (error) => {
                  console.error(error);
                  this.userSubject.next(null);
                  this.isLoggedInSubject.next(false);
                }
              )
              .finally(() => {
                this._isReady.next(true);
              });
          })
          .catch((error) => {
            console.error("Result not valid", error);
            this.userSubject.next(null);
            this.isLoggedInSubject.next(false);
            throw error;
          });
      } else {
        console.error("Result not valid");
        this.userSubject.next(null);
        this.isLoggedInSubject.next(false);
        this.claims.reset();
        this._isReady.next(true);
      }
    });
  }

  // #endregion Constructors (1)

  // #region Public Methods (3)

  public getCurrentLoggedInStatus(): boolean {
    return this.isLoggedInSubject.value;
  }

  public signOut() {
    const auth = getAuth();
    return signOut(auth).then((result) => {
      this.claims.reset();
      this.isLoggedInSubject.next(false);
    });
  }

  public async waitForReady(): Promise<boolean> {
    try {
      if (this._isReady.value) {
        return true;
      }
      const value = await lastValueFrom(
        this._isReady.pipe(
          filter((value) => value === true),
          first()
        )
      );
      return true;
    } catch (error) {
      console.error("waitForReady", error);
      return false;
    }
  }

  // #endregion Public Methods (3)
  async reset(email: string): Promise<void> {
    return await sendPasswordResetEmail(this.auth, email);
  }

  async register(email: string, password: string): Promise<User | null> {
    return createUserWithEmailAndPassword(this.auth, email, password).then(
      (result) => result.user
    );
  }

  async login(email: string, password: string): Promise<User | null> {
    return signInWithEmailAndPassword(this.auth, email, password).then(
      (result) => {
        if (result) {
          this.isLoggedInSubject.next(true);
        } else {
          this.isLoggedInSubject.next(false);
        }
        console.log("Logged in status", this.isLoggedInSubject.value);
        return result.user;
      }
    );
  }

  async loginWithGoogle(): Promise<User | null> {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(this.auth, provider)
      .then((result) => {
        if (result) {
          this.isLoggedInSubject.next(true);
        } else {
          this.isLoggedInSubject.next(false);
        }

        console.log("Logged in status", this.isLoggedInSubject.value);
        return result.user;
      })
      .catch((error) => {
        console.error("Login with Google failed:", error);
        throw error;
      });
  }
}
